import React, { useState } from 'react';
import { useOutletContext } from 'react-router-dom';

import { Collaborator } from '@/features/Collaborators/Models/collaborator';
import {
    CollaboratorStatusManagementTableView,
    PresetScopeRenderer,
    useColumnVisibility
} from '@/features/Collaborators/List/Contexts/StatusManagement';
import CollaboratorFilters from '@/features/Collaborators/List/Filters/CollaboratorFilters';
import NewAdvancedFiltersModal from '@/features/Collaborators/List/Filters/NewAdvancedFiltersModal';
import { ColumnVisibilityModal } from '@/features/Collaborators/List/Contexts/StatusManagement/ColumnVisibilityModal';
import { useFilterSystem } from '@/hooks/useFilterSystem';
import { ALL_FILTER_CONFIGS, ALL_FIELD_ACCESSORS, updateFilterConfigsWithData } from '@/features/Collaborators/List/Contexts/StatusManagement/configs/filterConfigsCombined';
import { useCapabilityCatalogQuery } from '@/features/Collaborators/Data';
import { resolveCollaboratorResources } from '@/features/Collaborators/utils/resourcePresentation';

type CollaboratorContext = {
    collaborators: Collaborator[];
    isLoading: boolean;
    error: Error | null;
};

export default function CollaboratorsStatusManagementPage() {
    // --- Base Data ---
    const { collaborators: initialCollaborators, isLoading: isLoadingCollaborators, error } = useOutletContext<CollaboratorContext>();
    const capabilityCatalogQuery = useCapabilityCatalogQuery();
    const collaboratorsWithResolvedResources = React.useMemo(
        () => initialCollaborators.map((collaborator) => resolveCollaboratorResources(collaborator, capabilityCatalogQuery.data)),
        [initialCollaborators, capabilityCatalogQuery.data]
    );

    // --- Column Visibility Logic ---
    const { 
        detectActivePreset, 
        applyPreset,
        getPresets,
        getAvailableColumns,
        toggleAllColumns,
        resetToDefault,
        columnVisibility,
        setColumnVisibility
    } = useColumnVisibility();
    const activePreset = detectActivePreset();

    // --- Filter Configurations (estável para evitar loops) ---
    const filterConfigs = React.useMemo(() => {
        return updateFilterConfigsWithData(ALL_FILTER_CONFIGS, collaboratorsWithResolvedResources, ALL_FIELD_ACCESSORS);
    }, [collaboratorsWithResolvedResources]);

    // --- Acessores para campos complexos (memoizados para evitar re-renders) ---
    const fieldAccessors = React.useMemo(() => ({
        // Todos os acessores combinados (Lotes 1, 2 e 3)
        ...ALL_FIELD_ACCESSORS
    }), []);

    // --- NOVO SISTEMA UNIFICADO ---
    const filterSystem = useFilterSystem(collaboratorsWithResolvedResources, {
        availableFilters: filterConfigs,
        urlSync: true,
        fieldAccessors
    });

    // --- Modal States ---
    const [isAdvancedFiltersModalOpen, setIsAdvancedFiltersModalOpen] = useState(false);
    const [isColumnVisibilityModalOpen, setIsColumnVisibilityModalOpen] = useState(false);

    if (isLoadingCollaborators) {
        return <div>Carregando dados...</div>
    }

    if (error) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="text-red-500 text-lg font-medium mb-2">
                        Erro ao carregar dados
                    </div>
                    <div className="text-gray-600 text-sm">
                        {error.message || 'Ocorreu um erro inesperado. Tente recarregar a página.'}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">


            {/* Active Filters Bar */}
            {filterSystem.hasActiveFilters && (
                <div className="mb-4">
                    <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm font-medium text-gray-700">Filtros Ativos:</span>
                        <button
                            onClick={filterSystem.clearAllFilters}
                            className="text-xs text-gray-600 hover:text-gray-800"
                        >
                            (limpar todos)
                        </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {filterSystem.getActiveFilterChips().map((chip) => (
                            <div key={chip.filterId} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 border border-blue-300 rounded-full text-xs">
                                <span className="font-medium text-blue-700">{chip.label}:</span>
                                <span className="text-blue-600">{chip.displayValue}</span>
                                <button
                                    onClick={chip.onRemove}
                                    className="ml-1 text-blue-400 hover:text-blue-600 rounded-full"
                                >
                                    ×
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Filters Section */}
            <CollaboratorFilters
                nameSearchQuery={filterSystem.globalFilter}
                onNameSearchChange={(e) => filterSystem.setGlobalFilter(e.target.value)}
                onOpenAdvancedFilters={() => setIsAdvancedFiltersModalOpen(true)}
                onOpenColumnSelector={() => setIsColumnVisibilityModalOpen(true)}
                activityStatusFilter={filterSystem.activityStatus}
                onActivityStatusFilterChange={filterSystem.setActivityStatus}
                collaborators={filterSystem.baseFilteredData}
            />

            {/* Conditional Scope Component based on Active Preset */}
            <PresetScopeRenderer
                activePreset={activePreset}
                collaborators={filterSystem.baseFilteredData}
                onCardFilterChange={filterSystem.setCardFilter}
            />

            {/* Table Section - DADOS JÁ FILTRADOS */}
            <CollaboratorStatusManagementTableView
                collaborators={filterSystem.fullFilteredData}
                isLoading={isLoadingCollaborators}
                columnVisibility={columnVisibility}
            />

            {/* Modals */}
            <NewAdvancedFiltersModal
                isOpen={isAdvancedFiltersModalOpen}
                onClose={() => setIsAdvancedFiltersModalOpen(false)}
                filters={filterSystem.advancedFilters}
                availableFilters={filterConfigs}
                onAddFilter={filterSystem.addAdvancedFilter}
                onUpdateFilter={filterSystem.updateAdvancedFilter}
                onRemoveFilter={filterSystem.removeAdvancedFilter}
                onClearAllFilters={filterSystem.clearAdvancedFilters}
                onApplyFilters={() => setIsAdvancedFiltersModalOpen(false)}
            />

            <ColumnVisibilityModal
                isOpen={isColumnVisibilityModalOpen}
                onClose={() => setIsColumnVisibilityModalOpen(false)}
                availableColumns={getAvailableColumns()}
                presets={getPresets()}
                columnVisibility={columnVisibility}
                onToggleColumn={(columnId) => {
                    setColumnVisibility(prev => ({
                        ...prev,
                        [columnId]: prev[columnId] === false ? true : false
                    }));
                }}
                onApplyPreset={applyPreset}
                onToggleAllColumns={toggleAllColumns}
                onResetToDefault={resetToDefault}
            />

        </div>
    );
} 
